// src/components/layout/AppShell.jsx
import React from "react";
import { T } from "../ui";
import NotificationBell from "../ui/NotificationBell";
import { useAuth } from "../../contexts/AuthContext";
import { logout } from "../../services/authService";
import toast from "react-hot-toast";
import { LogOut, Car, User } from "lucide-react";

const ROLE_LABEL = { admin: "المدير", captain: "كابتن", client: "عميل" };
const ROLE_COLOR = { admin: "#7C3AED", captain: "#0EA5E9", client: "#10B981" };

export default function AppShell({ tabs, activeTab, onTabChange, children }) {
  const { user } = useAuth();
  const accent = ROLE_COLOR[user?.role] || "#F97316";

  const handleLogout = async () => {
    await logout();
    toast.success("تم تسجيل الخروج");
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#F1F5F9",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      direction: "rtl",
      paddingBottom: "110px"
    }}>
      {/* ── Top Bar ── */}
      <header style={{
        background: "rgba(15, 23, 42, 0.85)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        height: 70,
        padding: "0 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        boxShadow: "0 4px 30px rgba(0,0,0,0.1)",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12,
            background: `linear-gradient(135deg, ${accent}, #1e293b)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 8px 20px ${accent}44`,
          }}>
            <Car size={22} color="white" strokeWidth={2.5} />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ color: "white", fontWeight: 900, fontSize: 18, letterSpacing: "-0.5px" }}>رايدو</span>
            <span style={{ color: accent, fontSize: 11, fontWeight: "800", marginTop: -2 }}>{ROLE_LABEL[user?.role]}</span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "rgba(255,255,255,0.08)",
            padding: "5px 5px 5px 15px",
            borderRadius: "50px",
            border: "1px solid rgba(255,255,255,0.1)",
          }}>
            {user?.photoUrl ? (
              <img src={user.photoUrl} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
            ) : (
              <div style={{
                width: 32, height: 32, borderRadius: "50%",
                background: accent, display: "flex", alignItems: "center",
                justifyContent: "center", color: "white"
              }}>
                <User size={16} />
              </div>
            )}
            <span style={{ color: "white", fontSize: 13, fontWeight: "700" }}>{user?.name?.split(' ')[0]}</span>
          </div>
          <NotificationBell />
          <button onClick={handleLogout} style={{
            width: 38, height: 38, borderRadius: 12, border: "none",
            background: "rgba(239, 68, 68, 0.15)", color: "#FF6B6B",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <LogOut size={19} />
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 650, margin: "0 auto", padding: "25px 16px" }}>
        {children}
      </main>

      {/* ── Floating Navigation Bar (نفس تصميمك الأصلي مع معالجة الألوان) ── */}
      <nav style={{
        position: "fixed",
        bottom: "20px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "92%",
        maxWidth: "400px",
        height: "75px",
        background: "rgba(255, 255, 255, 0.7)",
        backdropFilter: "blur(25px) saturate(200%)",
        WebkitBackdropFilter: "blur(25px) saturate(200%)",
        borderRadius: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-evenly",
        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        border: "1px solid rgba(255,255,255,0.5)",
        zIndex: 1000,
      }}>
        {tabs.map((tab) => {
          const active = activeTab === tab.key;
          const isReactElement = React.isValidElement(tab.icon);

          // هنا التعديل: استخراج اللون من الأيقونة التي عرفتها في ملف العميل
          const tabColor = (isReactElement && tab.icon.props.color) ? tab.icon.props.color : accent;

          return (
            <button
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "70px",
                position: "relative",
              }}
            >
              {/* Icon Container */}
              <div style={{
                width: active ? "52px" : "44px",
                height: active ? "52px" : "44px",
                borderRadius: "18px",
                // استخدام لون التبويب المحدد
                background: active ? tabColor : `${tabColor}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                transform: active ? "translateY(-22px)" : "translateY(0)",
                boxShadow: active ? `0 12px 24px ${tabColor}55` : "none",
                border: active ? "1px solid rgba(255,255,255,0.3)" : `1px solid ${tabColor}10`,
              }}>
                {isReactElement ? React.cloneElement(tab.icon, {
                  size: active ? 24 : 22,
                  color: active ? "white" : tabColor, // أيقونة بيضاء عند النشاط وملونة عند الخمول
                  strokeWidth: active ? 2.5 : 2
                }) : (
                  <span style={{ fontSize: active ? 24 : 22 }}>{tab.icon}</span>
                )}
              </div>

              {/* Label */}
              <span style={{
                fontSize: "11px",
                fontWeight: active ? "800" : "600",
                color: active ? "#0F172A" : "#64748B",
                marginTop: active ? "-16px" : "4px",
                transition: "all 0.3s ease",
              }}>
                {tab.label}
              </span>

              {active && (
                <div style={{
                  position: "absolute", bottom: -6, width: 12, height: 4,
                  background: tabColor, borderRadius: 10, animation: "pop 0.3s forwards"
                }} />
              )}
            </button>
          );
        })}
      </nav>

      <style>{`
        @keyframes pop {
          0% { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
}